// One day's bookings, folded into one card per job.
//
// Extracted from AgendaView in parity pass 3 batch 3, because the week and
// month tiles adopted the same per-job shape and two implementations of "what
// is a job's day" would disagree the first time one changed. This is the
// client-side sibling of ZT's groupByJob (Schedule.jsx 146-175), carrying the
// same aggregation rules: hours are the day's earliest start and latest
// finish across the job's bookings, crew and plant are de-duplicated display
// labels, and maintenance gets a group each because it has no job to group
// under — two machines at two different mechanics are not one entry.
import { isCrewKind } from './resourceGroups';
import { compactName } from './dates';
import type { CalendarBooking } from '../schedulingApi';

export interface JobDayGroup {
  key: string;
  jobId: string | null;
  /** Null on maintenance — kept apart from `subtitle` because it is what the
      colour is drawn from, and the two only coincide on a job. */
  jobNumber: string | null;
  /** The strong line. A maintenance booking has no job, so it says so. */
  title: string;
  /** The quiet line under it: the job number, or the supplier holding the machine. */
  subtitle: string | null;
  clientName: string | null;
  /** Earliest start and latest finish across the job's bookings that day. */
  startTime: string | null;
  endTime: string | null;
  /** People, compacted ("N Zanetto") — dense cells are why. */
  crew: string[];
  /** Machines, name plus identifier; the rego variant is agenda-only. */
  plant: string[];
  /** The agenda's fuller plant labels, rego included — the agenda is what
      crew read on site, where the plate matters (ZT's rule, kept). */
  plantWithRego: string[];
  /** Every resource, uncompacted — month chips and tooltips. */
  resources: string[];
  bookings: CalendarBooking[];
  inConflict: boolean;
}

/** "Water cart (WC-01)" — Dayboard's label order, no rego. */
function machineLabel(booking: CalendarBooking): string {
  const id = booking.resourceIdentifier?.trim();
  return id ? `${booking.resourceName} (${id})` : booking.resourceName;
}

/**
 * "Water cart (WC-01, Rego XT17AZ)" — identifier and plate in ONE
 * parenthetical, because these lists are comma-joined and the reminders'
 * em-dash form would read the plate as another machine. "Rego" is spelled
 * out so the parenthetical cannot be misread as a second identifier.
 */
function machineLabelWithRego(booking: CalendarBooking): string {
  const identifier = booking.resourceIdentifier?.trim();
  const rego = booking.resourceRegistration?.trim();

  const inside: string[] = [];
  if (identifier) inside.push(identifier);
  if (rego) inside.push(`Rego ${rego}`);

  return inside.length > 0
    ? `${booking.resourceName} (${inside.join(', ')})`
    : booking.resourceName;
}

export function groupDayByJob(bookings: CalendarBooking[]): JobDayGroup[] {
  const map = new Map<string, JobDayGroup>();

  for (const booking of bookings) {
    const key = booking.jobId ?? `maintenance:${booking.id}`;
    let group = map.get(key);

    if (!group) {
      group = {
        key,
        jobId: booking.jobId,
        jobNumber: booking.jobNumber,
        title: booking.jobTitle ?? 'Maintenance',
        // The supplier is the whole point of a maintenance card: "Maintenance"
        // says the machine is away, and only the supplier says who to ring.
        subtitle: booking.jobNumber ?? booking.supplierName,
        clientName: booking.clientName,
        startTime: booking.startTime,
        endTime: booking.endTime,
        crew: [],
        plant: [],
        plantWithRego: [],
        resources: [],
        bookings: [],
        inConflict: false,
      };
      map.set(key, group);
    }

    // The group's hours are the job's hours that day, not any one booking's:
    // earliest start, latest finish. A card reading 07:00–15:00 when the
    // excavator leaves at noon would be a claim nobody made.
    if (booking.startTime && (!group.startTime || booking.startTime < group.startTime)) {
      group.startTime = booking.startTime;
    }
    if (booking.endTime && (!group.endTime || booking.endTime > group.endTime)) {
      group.endTime = booking.endTime;
    }

    if (isCrewKind(booking.resourceKind)) {
      const label = compactName(booking.resourceName);
      if (!group.crew.includes(label)) group.crew.push(label);
    } else {
      const label = machineLabel(booking);
      if (!group.plant.includes(label)) group.plant.push(label);
      const full = machineLabelWithRego(booking);
      if (!group.plantWithRego.includes(full)) group.plantWithRego.push(full);
    }
    const plain = booking.resourceName;
    if (!group.resources.includes(plain)) group.resources.push(plain);

    group.bookings.push(booking);

    // One flagged booking flags the group. A job with a clash in it needs
    // re-planning whether or not the other four resources are fine.
    if (booking.inConflict) group.inConflict = true;
  }

  // Earliest first; anything with no time sits at the end, tie-broken on the
  // subtitle so the order is stable between renders rather than insertion-order
  // luck.
  return [...map.values()].sort((a, b) => {
    if (a.startTime && b.startTime) return a.startTime.localeCompare(b.startTime);
    if (a.startTime) return -1;
    if (b.startTime) return 1;
    return (a.subtitle ?? '').localeCompare(b.subtitle ?? '');
  });
}
